import {
  type CategoryOption,
  isCategoryNameOnly,
  toEnglishCategoryName,
  translateCategoryName,
} from "portal/utils/categoryCollection/category";
import {
  PlantProfileCategoryAutocomplete,
  type PlantProfileCategoryAutocompleteValue,
} from "portal/components/categoryCollectionProfile/PlantProfileCategoryAutocomplete";
import { TFunction } from "i18next";
import { useField } from "formik";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import React, { FC } from "react";

interface PlantProfileCategoryOptionsFieldProps {
  name: string;
  label: string;
  categoryOptions: CategoryOption[];
  loading?: boolean;
  onLoadMore?: () => void;
  onSearchInputChange: (text: string) => void;
  multiple?: boolean;
  freeSolo?: boolean;
  error?: boolean;
  helperText?: string;
}

const normalizeCategoryValue = (
  value: PlantProfileCategoryAutocompleteValue,
  multiple: boolean,
  t: TFunction
): CategoryOption | CategoryOption[] | null => {
  if (multiple) {
    const rawValues = Array.isArray(value) ? value : [];
    return rawValues.map((category) => {
      if (isCategoryNameOnly(category)) {
        const englishName = toEnglishCategoryName(t, category);
        return {
          id: uuid(),
          versionId: "",
          name: englishName,
          displayName: translateCategoryName(t, englishName),
          chipIds: [],
        };
      }
      return category;
    });
  }

  return value as CategoryOption | null;
};

export const PlantProfileCategoryOptionsField: FC<
  PlantProfileCategoryOptionsFieldProps
> = ({
  name,
  label,
  categoryOptions,
  loading = false,
  onLoadMore,
  onSearchInputChange,
  multiple = false,
  freeSolo = false,
  error,
  helperText,
}) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField<
    CategoryOption[] | CategoryOption | null
  >(name);

  const showError = error ?? (meta.touched && Boolean(meta.error));
  const formattedFieldError =
    meta.error &&
    (typeof meta.error === "string" ? meta.error : JSON.stringify(meta.error));
  const resolvedHelperText =
    helperText ?? (meta.touched ? formattedFieldError : undefined);

  return (
    <div className="mt-2">
      <PlantProfileCategoryAutocomplete
        name={field.name}
        label={label}
        categoryOptions={categoryOptions}
        loading={loading}
        multiple={multiple}
        freeSolo={freeSolo}
        error={showError}
        helperText={resolvedHelperText}
        // MUI Autocomplete uses null for an empty single-select value.
        // eslint-disable-next-line unicorn/no-null
        value={field.value ?? (multiple ? [] : null)}
        onBlur={field.onBlur}
        onSearchInputChange={onSearchInputChange}
        onLoadMore={onLoadMore}
        onChange={(value) => {
          helpers
            .setValue(normalizeCategoryValue(value, multiple, t), true)
            .finally(() => helpers.setTouched(true, false));
        }}
      />
    </div>
  );
};
